/**
 * Sync command - manually sync project status to GitHub Projects and Issues
 */

import chalk from 'chalk';
import { syncToGitHub, syncProgressToGitHub, detectGitRepo, getGitHubToken } from '../sync/github-sync.js';
import { findProgressFile, parseProgressFile } from '../sync/progress-parser.js';

interface SyncOptions {
  full?: boolean;
  issuesOnly?: boolean;
  projectOnly?: boolean;
  dryRun?: boolean;
  verbose?: boolean;
}

export async function syncCommand(repoArg?: string, options: SyncOptions = {}): Promise<void> {
  console.log(chalk.bold('\n🔄 Syncing project status to GitHub\n'));

  // Determine repository
  let repo = repoArg;
  if (!repo) {
    const detectedRepo = await detectGitRepo();
    if (!detectedRepo) {
      console.error(chalk.red('Error: Could not detect GitHub repository.'));
      console.log(chalk.dim('Specify repository explicitly: spacecode sync owner/repo'));
      console.log(chalk.dim('Or run from within a git repository with a GitHub origin remote.'));
      process.exit(1);
    }
    repo = detectedRepo;
  }

  // Validate repo format
  const repoMatch = repo.match(/^([^/]+)\/([^/]+)$/);
  if (!repoMatch) {
    console.error(chalk.red(`Error: Invalid repository format: ${repo}`));
    console.log(chalk.dim('Expected format: owner/repo'));
    process.exit(1);
  }

  console.log(chalk.dim(`Repository: ${repo}`));

  // Get GitHub token
  const token = await getGitHubToken();
  if (!token) {
    console.error(chalk.red('Error: GitHub token not found.'));
    console.log(chalk.dim('Set GITHUB_TOKEN environment variable or authenticate with: gh auth login'));
    process.exit(1);
  }

  // Dry run mode
  if (options.dryRun) {
    console.log(chalk.yellow('⚠️  DRY RUN MODE - No changes will be made\n'));
  }

  // Parse PROGRESS.md
  console.log(chalk.dim('Parsing PROGRESS.md...'));
  const progressFile = findProgressFile();
  const progressData = await parseProgressFile(progressFile, {
    verbose: options.verbose,
    onProgress: (msg) => {
      if (options.verbose) console.log(chalk.dim(`  ${msg}`));
    },
  });

  console.log(chalk.dim(`Found ${progressData.features.length} features`));
  console.log(chalk.dim(`  ${progressData.completedFeatures.length} completed`));
  console.log(chalk.dim(`  ${progressData.inProgressFeatures.length} in progress`));
  console.log(chalk.dim(`  ${progressData.notStartedFeatures.length} not started`));

  // Sync to GitHub based on options
  const totalResult = {
    issuesCreated: 0,
    issuesUpdated: 0,
    issuesClosed: 0,
    todosReplaced: 0,
    errors: [] as string[],
  };

  // Sync TODO comments (issues)
  if (!options.projectOnly) {
    console.log(chalk.dim('\nSyncing TODO comments to GitHub...'));
    const todoResult = await syncToGitHub(repo, token, {
      dryRun: options.dryRun,
      verbose: options.verbose,
      onProgress: (msg) => console.log(chalk.dim(`  ${msg}`)),
    });
    totalResult.issuesCreated += todoResult.issuesCreated;
    totalResult.issuesUpdated += todoResult.issuesUpdated;
    totalResult.issuesClosed += todoResult.issuesClosed;
    totalResult.todosReplaced += todoResult.todosReplaced;
    totalResult.errors.push(...todoResult.errors);
  }

  // Sync PROGRESS.md features (project)
  if (!options.issuesOnly) {
    console.log(chalk.dim('\nSyncing PROGRESS.md features to GitHub...'));
    const progressResult = await syncProgressToGitHub(repo, token, progressData, {
      dryRun: options.dryRun,
      verbose: options.verbose,
      onProgress: (msg) => console.log(chalk.dim(`  ${msg}`)),
    });
    totalResult.issuesCreated += progressResult.issuesCreated;
    totalResult.issuesClosed += progressResult.issuesClosed;
    totalResult.errors.push(...progressResult.errors);
  }

  const result = totalResult;

  // Report results
  console.log();
  if (options.dryRun) {
    console.log(chalk.yellow.bold('DRY RUN RESULTS:'));
  } else {
    console.log(chalk.bold('RESULTS:'));
  }

  if (result.issuesCreated > 0) {
    console.log(chalk.green(`  ✅ Created ${result.issuesCreated} issue(s)`));
  }
  if (result.issuesUpdated > 0) {
    console.log(chalk.green(`  ✅ Updated ${result.issuesUpdated} issue(s)`));
  }
  if (result.issuesClosed > 0) {
    console.log(chalk.green(`  ✅ Closed ${result.issuesClosed} issue(s)`));
  }
  if (result.todosReplaced > 0) {
    console.log(chalk.green(`  ✅ Replaced ${result.todosReplaced} TODO(s) with issue references`));
  }

  if (result.issuesCreated === 0 && result.issuesUpdated === 0 && result.issuesClosed === 0 && result.todosReplaced === 0) {
    console.log(chalk.dim('  No changes needed'));
  }

  if (result.errors.length > 0) {
    console.log();
    console.log(chalk.yellow.bold(`⚠️  ${result.errors.length} error(s):`));
    result.errors.forEach((err) => console.log(chalk.red(`  • ${err}`)));
  }

  console.log();
  if (options.dryRun) {
    console.log(chalk.yellow('Run without --dry-run to apply changes.'));
  }
}
